import { type EnvMap, type EnvPolicyMode, type EnvWarnFn, resolveTokenizedEnumEnv } from '@/shared/env-parsing';

export type AppEnvironmentMode = 'production' | 'test' | 'development';
export type AppLogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error';

export type RuntimeConfig = Readonly<{
    nodeEnv: AppEnvironmentMode;
    isProduction: boolean;
    isTest: boolean;
    appLogLevel: AppLogLevel;
}>;

export const APP_LOG_LEVEL_VALUES = ['trace', 'debug', 'info', 'warn', 'error'] as const;
export const DEFAULT_LD_OBSERVABILITY_LOG_LEVEL: AppLogLevel = 'trace';
const APP_LOG_LEVEL_SEVERITY: Record<AppLogLevel, number> = {
    trace: 10,
    debug: 20,
    info: 30,
    warn: 40,
    error: 50,
};

const normalizeNodeEnv = (env: EnvMap): AppEnvironmentMode => {
    const normalized = env.NODE_ENV?.trim().toLowerCase();
    if (normalized === 'production' || normalized === 'test') {
        return normalized;
    }

    return 'development';
};

const resolveDefaultAppLogLevel = (nodeEnv: AppEnvironmentMode): AppLogLevel => {
    return nodeEnv === 'production' ? 'info' : 'debug';
};

export const normalizeAppLogLevel = (level: unknown): AppLogLevel => {
    switch (level) {
        case 'trace':
            return 'trace';
        case 'debug':
            return 'debug';
        case 'warn':
            return 'warn';
        case 'error':
        case 'fatal':
            return 'error';
        default:
            return 'info';
    }
};

export const isAppLogLevelEnabled = ({
    level,
    minimumLevel,
}: {
    level: unknown;
    minimumLevel: AppLogLevel;
}): boolean => {
    const normalizedLevel = normalizeAppLogLevel(level);

    return APP_LOG_LEVEL_SEVERITY[normalizedLevel] >= APP_LOG_LEVEL_SEVERITY[minimumLevel];
};

export const resolveLaunchDarklyObservabilityLogLevel = ({
    env,
    policyMode = 'warn-default',
    warn,
    invalidWarning,
}: {
    env?: EnvMap;
    policyMode?: EnvPolicyMode;
    warn?: EnvWarnFn;
    invalidWarning?: (raw: string, fallback: AppLogLevel) => string;
} = {}): AppLogLevel =>
    resolveTokenizedEnumEnv({
        envKey: 'LD_OBSERVABILITY_LOG_LEVEL',
        allowedValues: APP_LOG_LEVEL_VALUES,
        defaultValue: DEFAULT_LD_OBSERVABILITY_LOG_LEVEL,
        env,
        policyMode,
        warn,
        invalidWarning,
    });

export const createRuntimeConfig = ({ env = process.env }: { env?: EnvMap } = {}): RuntimeConfig => {
    const nodeEnv = normalizeNodeEnv(env);

    return Object.freeze({
        nodeEnv,
        isProduction: nodeEnv === 'production',
        isTest: nodeEnv === 'test',
        appLogLevel: resolveTokenizedEnumEnv({
            envKey: 'LOG_LEVEL',
            allowedValues: APP_LOG_LEVEL_VALUES,
            defaultValue: resolveDefaultAppLogLevel(nodeEnv),
            env,
            policyMode: 'warn-default',
        }),
    });
};

export const runtimeConfig = createRuntimeConfig();
